"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useExperimentVariant } from "@/hooks/useExperimentVariant";
import { track } from "@/lib/analytics";
import { parseAbOverrides } from "@/lib/experimentOverride";
import { assets } from "@/lib/site";

const FLAG = "stats-photo-v2";

const alt =
  "Reiziger bekijkt in de trein de trein-vertraging.nl app met een claim van €142";

// Both shots have the rounded card and floating coins baked into a transparent image.
const shots = {
  shot1: { src: assets.statsShot1, width: 824, height: 1078 },
  shot2: { src: assets.statsShot2, width: 824, height: 1078 },
} as const;

// `stats-photo-v2` experiment: control is the hexagon cutout (SSR default), the
// shot variants swap in a rounded rectangular product photo after the flag loads.
//
// `stats_photo_viewed` is the experiment's exposure event, fired in every arm the
// first time half the photo is on screen. v1 enrolled on the flag evaluation at
// page load, but only ~7% of those visitors ever scrolled to this section, so its
// cta_click goal was almost entirely hero clicks the photo could not have caused.
// It waits for the PostHog variant (so the event carries `$feature/stats-photo-v2`)
// and skips `?ab=` previews so QA cannot enrol itself.
export function StatsPhoto({ layout }: { layout: "mobile" | "desktop" }) {
  const variant = useExperimentVariant(FLAG);
  const shot = variant === "shot1" || variant === "shot2" ? shots[variant] : null;
  const [photo, setPhoto] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!variant || !photo || !("IntersectionObserver" in window)) return;
    if (parseAbOverrides(window.location.search)[FLAG]) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        track("stats_photo_viewed", { variant, layout });
      },
      { threshold: 0.5 },
    );
    observer.observe(photo);
    return () => observer.disconnect();
  }, [variant, photo, layout]);

  if (layout === "mobile") {
    // The photo card inside the image (--card wide) lines up with the page
    // container's 1.25rem gutters, like the boxes around it; the coins spill past it. The image is 824px wide with
    // the 720px card starting at x=104, hence the 1.1444 and 0.1444 factors. The
    // band starts at the screen's left edge, so the margin is measured from 0.
    // data-stats-shot makes the section add room below it (Stats.tsx).
    if (shot) {
      return (
        <div
          ref={setPhoto}
          data-stats-shot
          className="absolute left-0 top-[calc(100%-13rem)] [--card:min(100vw_-_2.5rem,26rem)]"
        >
          <Image
            src={shot.src}
            alt={alt}
            width={shot.width}
            height={shot.height}
            sizes="(max-width: 460px) 115vw, 480px"
            className="ml-[calc((100vw_-_var(--card))/2_-_var(--card)*0.1444)] h-auto w-[calc(var(--card)*1.1444)] max-w-none"
          />
        </div>
      );
    }

    return (
      <div ref={setPhoto} className="absolute inset-x-0 bottom-[-13.5rem] h-96">
        {/* sizes is 360px, not 100vw: object-contain in a 24rem-tall box means
            the height decides the width, and at this aspect ratio the photo
            renders ~344px wide on every phone. */}
        <Image
          src={assets.statsHand}
          alt={alt}
          fill
          sizes="360px"
          className="object-contain object-bottom"
        />
      </div>
    );
  }

  if (shot) {
    return (
      <Image
        ref={setPhoto}
        src={shot.src}
        alt={alt}
        width={shot.width}
        height={shot.height}
        sizes="34vw"
        className="pointer-events-none absolute right-12 top-1/2 h-[120%] w-auto max-w-none -translate-y-1/2 object-contain"
      />
    );
  }

  return (
    <Image
      ref={setPhoto}
      src={assets.statsHand}
      alt={alt}
      width={992}
      height={1107}
      sizes="46vw"
      className="pointer-events-none absolute right-0 top-1/2 h-[132%] w-auto max-w-none -translate-y-1/2 object-contain"
    />
  );
}
